// src/pages/Department/ListDepartment.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { message } from "antd";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Breadcrumb from '../../components/Common/Breadcrumb';
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import FeatherIcon from "feather-icons-react";
import { fetchAllCarousels, updateCarouselSequences } from "../../services/carouselService";
import { encrypt } from '../../utils/encryption';
import { BASE_URL_IMAGE } from '../../config/api';
import { jwtDecode } from "jwt-decode";
import { fetchUserById } from "../../services/userService";

const ListCarousel = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [sequenceUpdates, setSequenceUpdates] = useState({});
  const [loggedInUserData, setLoggedInUserData] = useState(null); // 👈 added

  // Sort all images by sequence
  const sortedImages = [...carouselImages].sort((a, b) => a.carousel_sequence - b.carousel_sequence);

  // Filter featured and non-featured slides
  const activeSlides = sortedImages.filter(img => img.carousel_isFeatured === 1);
  const otherSlides = sortedImages.filter(img => img.carousel_isFeatured === 0);

  // Generate sequence options (1-10 or based on your needs)
  const sequenceOptions = Array.from({ length: 5 }, (_, i) => i + 1);

  const breadcrumbItems = [
    { label: "Carousel" },
    { label: "Carousel List" },
  ];

  // ✅ Fetch logged-in user (to get email)
  useEffect(() => {
    const fetchAndSetUser = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          const user = await fetchUserById(decoded.id);
          setLoggedInUserData(user);
        } catch (error) {
          console.error("Error decoding token or fetching user:", error);
          localStorage.removeItem("token");
          setLoggedInUserData(null);
        }
      }
    };

    fetchAndSetUser();
  }, []);

  useEffect(() => {
    const fetchCarouselData = async () => {
      try {
        setLoading(true);
        const data = await fetchAllCarousels();
        setCarouselImages(data);
        const initialUpdates = {};
        data.forEach(img => {
          initialUpdates[img.carousel_id] = img.carousel_sequence;
        });
        setSequenceUpdates(initialUpdates);
        messageApi.success("Carousel images fetched successfully");
      } catch (error) {
        messageApi.error("Failed to fetch carousel data");
      } finally {
        setLoading(false);
      }
    };

    fetchCarouselData();
  }, []);

  const usedSequences = useMemo(() => {
    return activeSlides
      .map(img => sequenceUpdates[img.carousel_id] || img.carousel_sequence)
      .filter(seq => seq !== 0); // Exclude inactive sequences
  }, [activeSlides, sequenceUpdates]);

  const handleSequenceChange = (id, value) => {
    const newSequence = parseInt(value);
    setSequenceUpdates(prev => ({
      ...prev,
      [id]: newSequence
    }));
  };

  const handleSubmitSequence = async () => {
    try {
      setSubmitLoading(true);
      
      // Prepare the data for submission
      const updates = Object.entries(sequenceUpdates).map(([id, sequence]) => ({
        carousel_id: parseInt(id),
        carousel_sequence: sequence
      }));

      // Validation 1: Check for featured slides with sequence 0
      const featuredSlidesWithZeroSequence = updates.filter(update => {
        const slide = carouselImages.find(img => img.carousel_id === update.carousel_id);
        return slide?.carousel_isFeatured === 1 && update.carousel_sequence === 0;
      });

      if (featuredSlidesWithZeroSequence.length > 0) {
        messageApi.error('Please assign sequence numbers to all featured slides before submission');
        return;
      }

      // Validation 2: Duplicate sequence check
      const activeSequences = updates
        .filter(update => update.carousel_sequence !== 0)
        .map(update => update.carousel_sequence);

      const duplicateSequences = activeSequences.filter((seq, index) => 
        activeSequences.indexOf(seq) !== index
      );

      if (duplicateSequences.length > 0) {
        const dupNumbers = [...new Set(duplicateSequences)].join(', ');
        messageApi.error(`Duplicate sequences detected (${dupNumbers}). Please assign unique numbers.`);
        return;
      }

      // Validation 3: Check for sequence numbers within allowed range
      const invalidSequences = activeSequences.filter(seq => seq < 1 || seq > sequenceOptions.length);
      if (invalidSequences.length > 0) {
        messageApi.error(`Sequence numbers must be between 1 and ${sequenceOptions.length}`);
        return;
      }

      // ✅ Add addedbyemail to the request data
      const requestData = {
        updates,
        addedbyemail: loggedInUserData?.user_email || 'unknown@user.com' // Fallback email
      };

      // Proceed with API call if all validations pass
      await updateCarouselSequences(requestData);
      messageApi.success("Sequences updated successfully");
      
      // Refresh data
      const data = await fetchAllCarousels();
      setCarouselImages(data);

      // Reset sequence updates to reflect the new server state
      const newSequenceUpdates = {};
      data.forEach(img => {
        newSequenceUpdates[img.carousel_id] = img.carousel_sequence;
      });
      setSequenceUpdates(newSequenceUpdates);

    } catch (error) {
      console.error("Error updating sequences:", error);
      messageApi.error(error.message || "Failed to update sequences");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlePreview = (image) => {
    setSelectedImage(image);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setSelectedImage(null);
    setShowPreview(false);
  };

  return (
    <div>
      <Helmet><title>List Carousel | CRM Jnec</title></Helmet>
      {contextHolder}
      <Header />
      <Sidebar activeClassName='list-carousel' />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12"><Breadcrumb items={breadcrumbItems} /></div>
            </div>
          </div>

          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-3">
                  <h4 className="text-primary mb-0 fw-bold">Active Slides</h4>
                  <span className="badge bg-light text-dark border px-3 py-2">
                    {activeSlides.length} / {activeSlides.length + otherSlides.length} Slides
                  </span>
                </div>
                <button 
                  className="btn btn-primary btn-lg px-4 py-2 shadow-sm"
                  onClick={handleSubmitSequence}
                  disabled={submitLoading}
                >
                  {submitLoading ? (
                    <>
                      <FeatherIcon icon="loader" size={18} className="me-2 spin" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <FeatherIcon icon="check-circle" size={18} className="me-2" />
                      Submit Sequence
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message="Fetching data..." />
          ) : (
            <>
              {/* Featured Slides */}
              {activeSlides.length > 0 && (
                <>
                  <div className="row g-4 mb-4">
                    {activeSlides.map((image) => (
                      <div key={image.carousel_id} className="col-sm-6 col-md-6 col-xl-4 col-xxl-3">
                        <div className={`carousel-card position-relative h-100 ${
                            image.carousel_isFeatured === 1 && 
                            (sequenceUpdates[image.carousel_id] || image.carousel_sequence) === 0 ? 
                            'border border-danger' : ''
                          }`}>
                          <div className="carousel-image-container position-relative overflow-hidden">
                            <img
                              className="carousel-image img-fluid w-100"
                              src={`${BASE_URL_IMAGE}/uploads/carouselImages/${image.carousel_img}`}
                              alt={image.carousel_title}
                            />
                            
                            {/* Active Status Badge */}
                            <div className="position-absolute top-0 end-0 m-3">
                              <span className="badge bg-success text-white">
                                <FeatherIcon icon="check-circle" size={12} className="me-1" />
                                Active
                              </span>
                            </div>
                            <div className="position-absolute top-0 start-0 m-3">
                              <span className="sequence-badge badge bg-warning text-dark fw-bold px-3 py-2">
                                Slide {image.carousel_sequence}
                              </span>
                            </div>
                            <div className="carousel-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                              <div className="d-flex gap-3">
                                <button
                                  className="btn btn-light btn-action rounded-circle shadow-lg"
                                  onClick={() => handlePreview(image)}
                                  title="Preview Image"
                                >
                                  <FeatherIcon icon="eye" size={16} />
                                </button>
                                <Link
                                  to={`/edit-carousel/${image.carousel_id}`}
                                  className="btn btn-light btn-action rounded-circle shadow-lg"
                                  title="Edit Image"
                                >
                                  <FeatherIcon icon="edit" size={16} />
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="carousel-content p-4">
                            <h6 className="carousel-title mb-3 text-dark fw-semibold">
                              {image.carousel_title}
                            </h6>
                            <div className="d-flex align-items-center">
                              <label className="form-label me-2 mb-0 text-muted small fw-medium">
                                Sequence:
                              </label>
                              <select
                                value={sequenceUpdates[image.carousel_id] || image.carousel_sequence}
                                onChange={(e) => handleSequenceChange(image.carousel_id, e.target.value)}
                                className="form-select form-select-sm sequence-select"
                              >
                                <option value="0">Inactive (0)</option>
                                {sequenceOptions.map(num => (
                                  <option key={num} value={num}>
                                    {num}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Non-Featured Slides */}
              {otherSlides.length > 0 && (
                <>
                  <div className="d-flex align-items-center gap-3">
                    <h4 className="mb-3 mt-4 text-muted fw-bold">Inactive Slides</h4>
                    <span className="badge bg-light text-dark border px-3 py-2">
                      {otherSlides.length} / {activeSlides.length + otherSlides.length} Slides
                    </span>
                  </div>
                  <div className="row g-4">
                    {otherSlides.map((image) => (
                      <div key={image.carousel_id} className="col-sm-6 col-md-6 col-xl-4 col-xxl-3">
                        <div className="carousel-card position-relative h-100">
                          <div className="carousel-image-container position-relative overflow-hidden">
                            <img
                              className="carousel-image img-fluid w-100"
                              src={`${BASE_URL_IMAGE}/uploads/carouselImages/${image.carousel_img}`}
                              alt={image.carousel_title}
                            />
                            {/* Inactive Status Badge */}
                            <div className="position-absolute top-0 end-0 m-3">
                              <span className="badge bg-secondary text-white">
                                <FeatherIcon icon="slash" size={12} className="me-1" />
                                Inactive
                              </span>
                            </div>
                            <div className="position-absolute top-0 start-0 m-3">
                              <span className="sequence-badge badge bg-secondary text-white fw-bold px-3 py-2">
                                Slide {image.carousel_sequence}
                              </span>
                            </div>
                            <div className="carousel-overlay position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                              <div className="d-flex gap-3">
                                <button
                                  className="btn btn-light btn-action rounded-circle shadow-lg"
                                  onClick={() => handlePreview(image)}
                                  title="Preview Image"
                                >
                                  <FeatherIcon icon="eye" size={16} />
                                </button>
                                <Link
                                  to={`/edit-carousel/${encodeURIComponent(encrypt(image.carousel_id))}`}
                                  className="btn btn-light btn-action rounded-circle shadow-lg"
                                  title="Edit Image"
                                >
                                  <FeatherIcon icon="edit" size={16} />
                                </Link>
                              </div>
                            </div>
                          </div>
                          <div className="carousel-content p-4">
                            <h6 className="carousel-title mb-3 text-dark fw-semibold">
                              {image.carousel_title}
                            </h6>
                            <div className="d-flex align-items-center">
                              <label className="form-label me-2 mb-0 text-muted small fw-medium">
                                Sequence:
                              </label>
                              <select
                                value={0}
                                disabled
                                className="form-select form-select-sm sequence-select"
                                title={image.carousel_isFeatured === 0 ? 
                                      "Activate slide first to change sequence" : 
                                      "Select unique sequence number"}
                              >
                                <option value={0}>Inactive (0)</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && selectedImage && (
        <div className="modal show d-block carousel-modal" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 bg-primary-brown">
                <h5 className="modal-title fw-bold text-white">
                  <FeatherIcon icon="image" size={18} className="me-2" />
                  {selectedImage.carousel_title}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={handleClosePreview}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body p-0 text-center">
                <img
                  src={`${BASE_URL_IMAGE}/uploads/carouselImages/${selectedImage.carousel_img}`}
                  alt={selectedImage.carousel_title}
                  className="img-fluid w-100 preview-image"
                />
              </div>
              <div className="modal-footer border-0 bg-light-brown flex-column align-items-start">
                <p className="text-muted mb-3 lead">{selectedImage.carousel_description}</p>
                <div className="d-flex justify-content-between w-100 align-items-center">
                  <span className="badge bg-warning text-dark px-3 py-2 fw-bold">
                    <FeatherIcon icon="list" size={16} className="me-2" />
                    Sequence: #{selectedImage.carousel_sequence}
                  </span>
                  <div className="d-flex gap-2">
                    <Link to={`/edit-carousel/${selectedImage.carousel_id}`} className="btn btn-primary">
                      <FeatherIcon icon="edit" size={16} className="me-2" />
                      Edit
                    </Link>
                    <button className="btn btn-secondary" onClick={handleClosePreview}>
                      <FeatherIcon icon="x" size={16} className="me-2" />
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ListCarousel;